import { BodyBlockV2Item, EPosition, StoreBodyBlock } from 'constants/enums';
import { z } from 'zod';

import { GradientDirection } from 'components/AcGradientInput/AcGraidentInput.types';
import {
  ETemplateStudioBundlesViewModel,
  ETemplateStudioLayoutOptions,
  EThemes
} from 'components/TemplatesStudio/enums';

const gradientSchema = z.object({
  colorOne: z.string().min(1, { message: 'Color is required.' }),
  colorTwo: z.string().optional(),
  gradientDirection: z.nativeEnum(GradientDirection).optional()
});

export const templateStudioSchema = z.object({
  templateName: z.string().min(1, { message: 'This field is required.' }),
  description: z.string(),
  tags: z.array(z.string()),
  bodyBlocksOrder: z.array(z.nativeEnum(StoreBodyBlock)),
  bodyBlocksOrderV2: z.array(
    z.object({
      id: z.string().optional(),
      blocks: z.array(z.nativeEnum(StoreBodyBlock)),
      isVisible: z.boolean(),
      isMergeable: z.boolean()
    })
  ) as z.ZodType<BodyBlockV2Item[]>,
  header: z.object({
    showLogo: z.boolean(),
    showPlayerName: z.boolean(),
    showPlayerId: z.boolean(),
    isSticky: z.boolean(),
    backgroundOpacity: z
      .number()
      .min(0, { message: 'Opacity must be at least 0%' })
      .max(100, { message: 'Opacity must be at most 100%' }),
    textColor: gradientSchema
  }),
  promotion: z.object({
    themes: z.array(z.nativeEnum(EThemes)),
    selectedDesignId: z.string().nonempty('This field is required.')
  }),
  bundle: z.object({
    offersPerRow: z.nativeEnum(ETemplateStudioBundlesViewModel).nullable(),
    bundleViewModel: z.nativeEnum(ETemplateStudioBundlesViewModel),
    themes: z.array(z.nativeEnum(EThemes)),
    selectedDesignId: z.string().nonempty('This field is required.')
  }),
  popup: z.object({
    selectedDesignId: z.string().nonempty('This field is required.')
  }),
  rollingOffers: z.object({
    desktopLayout: z.nativeEnum(ETemplateStudioLayoutOptions),
    themes: z.array(z.nativeEnum(EThemes)),
    selectedDesignId: z.string().nonempty('This field is required.')
  }),
  buyOneGetMany: z
    .object({
      desktopLayout: z.nativeEnum(ETemplateStudioLayoutOptions),
      themes: z.array(z.nativeEnum(EThemes)),
      selectedDesignId: z.string().nonempty('This field is required.'),
      freeTilesLayout: z.union([z.literal(1), z.literal(2), z.literal(3)]),
      maxOffers: z
        .number()
        .int({ message: 'Max offers must be a whole number.' })
        .positive({ message: 'Max offers must be at least 1.' })
        .optional()
        .default(10),
      unlimited: z.boolean().optional().default(true)
    })
    .optional(),
  progressBar: z.object({
    selectedDesignId: z.string().nonempty('This field is required.')
  }),
  richText: z
    .object({
      text: z.string(),
      fontColor: z.string()
    })
    .optional(),
  dailyBonusBadge: z.object({
    themes: z.array(z.nativeEnum(EThemes)),
    selectedDesignId: z.string().nonempty('This field is required.')
  }),
  triggeredPopups: z.object({
    selectedDesignId: z.string().nonempty('This field is required.'),
    selectedBadgeDesignId: z.string().nonempty('This field is required.'),
    themes: z.array(z.nativeEnum(EThemes))
  }),
  rewardCalendar: z.object({
    selectedDesignId: z.string().nonempty('This field is required.'),
    selectedBadgeDesignId: z.string().nonempty('This field is required.'),
    themes: z.array(z.nativeEnum(EThemes))
  }),
  footer: z.object({
    showAppchargeLogo: z.boolean(),
    backgroundOpacity: z.number().min(0).max(100),
    textColor: gradientSchema
  }),
  brandKit: z.object({
    fontFamily: z.string(),
    buttonColor: gradientSchema,
    buttonTextColor: z.string(),
    logo: z.string().min(1, { message: 'This field is required.' }),
    backgroundImageMobile: z
      .string()
      .min(1, { message: 'This field is required.' }),
    backgroundImageDesktop: z
      .string()
      .min(1, { message: 'This field is required.' }),
    defaultBanner: z.string(),
    footerDesktopImage: z.string(),
    footerMobileImage: z.string(),
    customArrowImage: z.string().optional()
  }),
  externalButtons: z.object({
    verticalOffset: z
      .number()
      .min(0, { message: 'Vertical Offset must be at least 0%' })
      .max(100, { message: 'Vertical Offset must be at most 100%' }),
    addToHomeScreenButton: z
      .object({
        active: z.boolean(),
        borderColor: gradientSchema,
        backgroundColor: gradientSchema,
        buttonImage: z.string().optional(),
        iconImage: z.string().optional(),
        shortcutName: z.string().optional()
      })
      .refine(
        (data) => !data.active || (data.iconImage?.trim().length ?? 0) > 0,
        {
          message: 'Icon Image is required when the feature is activated.',
          path: ['iconImage']
        }
      )
      .refine(
        (data) => !data.active || (data.shortcutName?.trim().length ?? 0) > 0,
        {
          message: 'Store Title is required when the feature is activated.',
          path: ['shortcutName']
        }
      ),
    backToGameButton: z
      .object({
        active: z.boolean(),
        buttonImage: z.string().optional(),
        position: z.nativeEnum(EPosition)
      })
      .refine(
        (data) => !data.active || (data.buttonImage?.trim().length ?? 0) > 0,
        {
          message: 'Button Image is required when the feature is activated.',
          path: ['buttonImage']
        }
      )
      .refine(
        (data) => !data.active || (data.position?.trim().length ?? 0) > 0,
        {
          message: 'Position is required when the feature is activated.',
          path: ['position']
        }
      )
  })
});
